import React, { useState } from "react";
import { toast } from "react-toastify";
import { PrimaryButton, SecondaryButton } from "../../../UI";
import ConfirmModal from "../../../UI/Modal/confirmModal";
import { extractProductFeedSource, testProductFeedSource } from "../../../../utils/APIs";
import { Drawer, TabBar } from "../common";
import { PRODUCT_FEEDS_FUNCTION_ID, SOURCE_STEPS } from "../constants";
import { errorMessage } from "../helpers";
import RequestStep from "./RequestStep";
import StructureStep from "./StructureStep";
import MappingStep from "./MappingStep";
import RoleStep from "./RoleStep";

// Source drawer: request → structure → field mapping → role.
// Works on a local copy; "Apply" writes it into the feed draft.
const SourceDrawer = ({ source: initialSource, sources, draft, auth, meta, feedId, onApply, onModelChange, onClose }) => {
    const [source, setSource] = useState(initialSource);
    const [step, setStep] = useState("request");
    const [testResult, setTestResult] = useState(null);
    const [extractResult, setExtractResult] = useState(null);
    const [testing, setTesting] = useState(false);
    const [extracting, setExtracting] = useState(false);
    const [changed, setChanged] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);

    const update = (updater) => {
        setSource((previous) => (typeof updater === "function" ? updater(previous) : updater));
        setChanged(true);
    };

    // Both calls resolve to { data } or { error }; each step shows the outcome
    // next to the button that started it.
    // Secret values the user hasn't changed are sent masked; the server fills
    // them in from the saved draft (feed_id).
    const runTest = async () => {
        try {
            setTesting(true);
            const result = await testProductFeedSource({ ...auth, data: { function_id: PRODUCT_FEEDS_FUNCTION_ID, feed_id: feedId, source, model: draft.model } });
            setTestResult(result?.data);
            setExtractResult(null);
            return { data: result?.data };
        } catch (e) {
            return { error: errorMessage(e) };
        } finally {
            setTesting(false);
        }
    };

    const runExtract = async (override) => {
        try {
            setExtracting(true);
            const result = await extractProductFeedSource({ ...auth, data: { function_id: PRODUCT_FEEDS_FUNCTION_ID, feed_id: feedId, source: override || source, model: draft.model, sample_size: 20 } });
            setExtractResult(result?.data);
            return { data: result?.data };
        } catch (e) {
            const error = errorMessage(e);
            setExtractResult({ error });
            return { error };
        } finally {
            setExtracting(false);
        }
    };

    const apply = () => {
        const clash = sources.some((s) => s !== initialSource && s.id === source.id);
        if (clash) {
            toast.error(`Another source already uses the id "${source.id}".`);
            return;
        }
        onApply(source);
        toast.success(`"${source.name || source.id}" applied to the draft. Save the draft or publish to keep it.`);
        onClose();
    };

    const close = () => (changed ? setConfirmClose(true) : onClose());

    const stepProps = { source, update, sources, draft, meta, testResult, extractResult, testing, extracting, runTest, runExtract, onModelChange };

    return (
        <Drawer
            title={`Source: ${source.name || source.id}`}
            onClose={close}
            footer={
                <>
                    {changed && <span className="text-sm text-yellow-700 mr-auto self-center">Changes not applied to the draft yet</span>}
                    <SecondaryButton size="sm" handleClick={close}>Cancel</SecondaryButton>
                    <PrimaryButton size="sm" disable={!changed} handleClick={apply}>Apply to draft</PrimaryButton>
                </>
            }
        >
            <TabBar tabs={SOURCE_STEPS} active={step} onChange={setStep} className="mb-4" />
            {step === "request" && <RequestStep {...stepProps} onNext={() => setStep("structure")} />}
            {step === "structure" && <StructureStep {...stepProps} onNext={() => setStep("mapping")} />}
            {step === "mapping" && <MappingStep {...stepProps} />}
            {step === "role" && <RoleStep {...stepProps} />}

            {confirmClose && (
                <ConfirmModal
                    title="Discard source changes"
                    ConfirmText="Discard"
                    description="This source has changes that haven't been applied to the draft. Close it anyway?"
                    hideModal={() => setConfirmClose(false)}
                    handleConfirmation={onClose}
                />
            )}
        </Drawer>
    );
};

export default SourceDrawer;
